import { useRetry } from "@rpldy/retry-hooks";
import { BatchItem, FILE_STATES } from "@rpldy/shared";
import {
  useAbortItem,
  useBatchAddListener,
  useItemAbortListener,
  useItemCancelListener,
  useItemErrorListener,
  useItemFinalizeListener,
  useItemFinishListener,
  useItemProgressListener,
  useItemStartListener,
  useUploadyContext,
} from "@rpldy/uploady";
import log from "loglevel";
import React, {
  useCallback,
  useContext,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";

export interface FileUploadHandle {
  upload: () => void;
  clear: () => void;
}

export interface FileUploadControllerContextValue {
  items: BatchItem[];
  abortItem: (itemId: BatchItem) => void;
  retryItem: (itemId: BatchItem) => void;
}

const FileUploadControllerContext =
  React.createContext<FileUploadControllerContextValue>(undefined as never);

export const useFileUploadController = () =>
  useContext(FileUploadControllerContext);

export const FileUploadController: React.FC<
  React.PropsWithChildren<{
    handleRef?: React.Ref<FileUploadHandle>;
    onItemsChanged: (items: BatchItem[]) => void;
    onItemFinished?: (item: BatchItem) => void;
  }>
> = ({ handleRef, onItemsChanged, onItemFinished, children }) => {
  const { processPending } = useUploadyContext();

  const [items, setItems] = useState<BatchItem[]>([]);

  const abortItem = useAbortItem();
  const retryItem = useRetry();

  const itemListener = useCallback(
    (item: BatchItem) =>
      setItems((prev) => prev.map((i) => (i.id === item.id ? item : i))),
    [],
  );

  const onRetryItem = useCallback(
    (item: BatchItem) => {
      retryItem(item.id);
    },
    [retryItem],
  );
  const clearItems = () => setItems([]);

  const onRemoveItem = useCallback(
    (item: BatchItem) => {
      if (
        item.state !== FILE_STATES.FINISHED &&
        item.state !== FILE_STATES.ERROR &&
        item.state !== FILE_STATES.ABORTED
      ) {
        abortItem(item.id);
      }
      setItems((prev) => prev.filter((i) => i.id !== item.id));
    },
    [abortItem],
  );

  useItemAbortListener(itemListener);
  useItemCancelListener(itemListener);
  useItemErrorListener(itemListener);
  useItemFinalizeListener(itemListener);
  useItemStartListener(itemListener);
  useItemProgressListener(itemListener);

  useItemFinishListener((item) => {
    itemListener(item);
    onItemFinished?.(item);
  });

  useItemErrorListener((item) => {
    log.error("Upload error", item.uploadResponse, item);
  });

  useBatchAddListener((batch) => {
    setItems((prev) => {
      const newItems = [...prev];
      batch.items.forEach((item) => {
        const existingIndex = newItems.findIndex((i) => i.id === item.id);
        if (existingIndex !== -1) {
          newItems[existingIndex] = item;
        } else {
          newItems.push(item);
        }
      });
      return newItems;
    });
  });

  useLayoutEffect(() => {
    onItemsChanged(items);
  }, [items, onItemsChanged, onRemoveItem]);

  useImperativeHandle(handleRef, () => ({
    upload: processPending,
    clear: clearItems,
  }));

  const value = useMemo(
    () => ({ items, abortItem: onRemoveItem, retryItem: onRetryItem }),
    [items, onRemoveItem, onRetryItem],
  );

  return (
    <FileUploadControllerContext.Provider value={value}>
      {children}
    </FileUploadControllerContext.Provider>
  );
};

FileUploadController.displayName = "FileUploadController";
